import apiClient from './apiClient';
import * as userApi from './api/userApi';

/**
 * Service to manage users and roles via Yaseero Backend API.
 * This replaces direct Keycloak Admin access.
 */
export const keycloakAdminService = {
    /**
     * Fetch all users from the backend for current tenant
     */
    fetchUsers: async (params = {}) => {
        return await userApi.getAllUsers(params);
    },

    /**
     * Fetch all available roles from the backend
     */
    getRoles: async () => {
        try {
            const response = await apiClient.get('/roles');
            // Filter to only show relevant application roles
            const relevantRoles = ['TENANT_OWNER', 'TENANT_ADMIN', 'SALES_MANAGER', 'SALES_PERSON', 'USER', 'WAREHOUSE_PERSON'];
            return response.data
                .filter(role => relevantRoles.includes(role.name))
                .map(role => ({
                    id: role.id,
                    name: role.name,
                    description: role.description
                }));
        } catch (error) {
            console.error('Failed to fetch roles', error);
            // Fallback to hardcoded roles if API fails
            return [
                { name: 'TENANT_OWNER', description: 'Supplier - Full access to their tenant' },
                { name: 'TENANT_ADMIN', description: 'Super Portal Admin' },
                { name: 'SALES_MANAGER', description: 'Supervisor & Sales Manager' },
                { name: 'SALES_PERSON', description: 'Sales Staff' },
                { name: 'WAREHOUSE_PERSON', description: 'Warehouse Admin - Manages warehouse orders and dispatch' },
                { name: 'USER', description: 'User Role' }
            ];
        }
    },

    /**
     * Create a new user via backend
     */
    createUser: async (userData) => {
        // Map frontend userData to backend CreateUserRequest
        const request = {
            username: userData.username,
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            password: userData.credentials?.[0]?.value || 'Temporary123!',
            phone: userData.phone || '',
            employeeId: userData.employeeId || '',
            vehicleNumber: userData.vehicleNumber || '',
            userType: userData.userType || 'OWNER',
            roles: userData.roles || []
        };

        const response = await userApi.createUser(request);
        return { id: response.userId };
    },

    /**
     * Update user details via backend
     */
    updateUser: async (userId, userData) => {
        const request = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phone: userData.phone || null,
            employeeId: userData.employeeId || null,
            vehicleNumber: userData.vehicleNumber || null
        };
        await userApi.updateUser(userId, request);
        return true;
    },

    /**
     * Get roles for a user (Backend UserDTO already includes roles)
     */
    getUserRoles: async (userId) => {
        try {
            const response = await apiClient.get('/tenants/current/users');
            const user = response.data.find(u => u.id === userId);
            return user?.realmRoles?.map(role => ({ name: role })) || [];
        } catch (error) {
            console.error('Failed to fetch user roles', error);
            return [];
        }
    },

    /**
     * Assign roles to a user via backend
     */
    assignRole: async (userId, roles) => {
        for (const role of roles) {
            await userApi.assignRole(userId, role.name);
        }
        return true;
    },

    /**
     * Remove roles from a user via backend
     */
    removeRole: async (userId, roles) => {
        for (const role of roles) {
            await userApi.revokeRole(userId, role.name);
        }
        return true;
    },

    /**
     * Set user enabled/disabled
     */
    setUserEnabled: async (userId, enabled) => {
        await userApi.setUserEnabled(userId, enabled);
        return true;
    },

    /**
     * Transfer customers from one user to another
     */
    transferUserCustomers: async (userId, toUserId) => {
        return await userApi.transferUserCustomers(userId, toUserId);
    },

    /**
     * Reset a user's password
     */
    resetPassword: async (userId, newPassword) => {
        await userApi.resetPassword(userId, newPassword);
        return true;
    },

    /**
     * Delete a user
     * NOTE: Backend DELETE /api/user/{userId} endpoint is not yet implemented
     * As a workaround, we disable the user instead
     */
    deleteUser: async (userId) => {
        try {
            // TODO: Backend needs to implement DELETE /api/user/{userId}
            console.warn('Delete user endpoint not implemented in backend, disabling user instead');
            await keycloakAdminService.setUserEnabled(userId, false);
            return true;
        } catch (error) {
            console.error('Failed to delete user', error);
            throw error;
        }
    }
};
